/**
 * InpiRegularizationController — page `/inpi-regularisation.html` (EPIC-008
 * / 8.7).
 *
 * Cible : utilisateur dont l'INPI a renvoyé une régularisation sur sa
 * formalité de création (ou de modification). On affiche les éléments
 * demandés (`requirements` jsonb), on permet d'uploader un PDF par
 * requirement (`POST /inpi/regularization/{id}/upload`), puis on
 * débloque le bouton « Renvoyer » (`POST /inpi/regularization/{id}/submit`).
 *
 * Pour les modifications, le submit retourne `{redirectUrl}` (re-signature
 * sur procedures.inpi.fr). Pour les créations, retour au dashboard avec un
 * toast de confirmation (le re-dépôt INPI est déclenché manuellement par
 * un opérateur admin via le backoffice 8.8).
 *
 * Conventions :
 *   - Pas d'HTML inline en JS (clone du <template id="tpl-inpi-requirement">).
 *   - Aucune classe CSS inventée — toutes ⊆ notamicro.css.
 *   - Vocabulaire UI : « INPI », « procedures.inpi.fr » visibles ; pas
 *     de mention de NotaFacto / Sweego / SuperPDP (sans rapport ici).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const FORMALITY_TYPE_LABELS = {
  creation: "Création de micro-entreprise",
  modification: "Modification de micro-entreprise",
  update: "Modification de micro-entreprise",
  cessation: "Cessation de micro-entreprise",
};

const ERROR_MESSAGES = {
  validation_failed: "Données invalides. Vérifiez le fichier ou rechargez la page.",
  regularization_already_submitted: "Cette régularisation a déjà été traitée.",
  regularization_incomplete: "Veuillez fournir tous les éléments demandés avant de renvoyer la formalité.",
  pj_upload_failed: "Échec du téléversement de la pièce. Vérifiez le format (PDF) et la taille (≤ 10 Mo).",
  formality_not_found: "Cette formalité est introuvable.",
};

export default class InpiRegularizationController extends BaseController {
  static targets = [
    "loading",
    "error",
    "errorMessage",
    "content",
    "intro",
    "requirements",
    "submitBtn",
    "submitHelp",
    "inpiId",
    "formalityTypeLabel",
    "depositedAt",
    "regulReceivedAt",
  ];

  _formalityId = null;
  _formality = null;
  // Map<requirementId, {request, attachment|null}>
  _state = new Map();

  async connect() {
    this._formalityId = new URLSearchParams(window.location.search).get("formalityId");
    if (!this._formalityId) {
      this._showError("Paramètre `formalityId` manquant dans l'URL.");
      return;
    }
    await this._refresh();
  }

  // ---------------------------------------------------------------------------
  // Fetch + render
  // ---------------------------------------------------------------------------

  async _refresh() {
    this._showLoading();
    try {
      const data = await api.inpi.getFormality(this._formalityId);
      this._formality = data?.formality || null;
      const requests = Array.isArray(data?.regularizationRequests) ? data.regularizationRequests : [];
      const events = Array.isArray(data?.events) ? data.events : [];
      const attachments = Array.isArray(data?.attachments) ? data.attachments : [];

      const openRequests = requests.filter((r) => !r.resolvedAt);
      if (openRequests.length === 0) {
        this._showError("Aucune régularisation ouverte pour cette formalité.");
        return;
      }

      this._buildState(openRequests, events, attachments);
      this._renderHeader(requests);
      this._renderRequirements();
      this._showContent();
    } catch (err) {
      console.error("Failed to load formality", err);
      const code = err?.data?.error?.code;
      if (err?.status === 404) {
        this._showError("Cette formalité n'existe pas ou n'est pas accessible.");
      } else if (code && ERROR_MESSAGES[code]) {
        this._showError(ERROR_MESSAGES[code]);
      } else {
        this._showError(this._extractMessage(err) || "Impossible de charger les éléments demandés.");
      }
    }
  }

  _buildState(openRequests, events, attachments) {
    // Reconstruit les uploads passés en croisant events `regularization_uploaded`
    // (data.requirement_id, data.attachment_id) avec la liste actuelle des PJ.
    const attachmentsById = new Map(attachments.map((a) => [a.id, a]));
    const uploadsByRequirement = new Map();
    for (const ev of events) {
      if (ev.event !== "regularization_uploaded") continue;
      const reqId = ev.data?.requirement_id;
      const attId = ev.data?.attachment_id;
      if (!reqId || !attId) continue;
      const att = attachmentsById.get(attId);
      if (!att || att.deletedAt) continue;
      // Un seul upload conservé par requirement (le plus récent — events
      // listés antéchronologique côté repo).
      if (!uploadsByRequirement.has(reqId)) {
        uploadsByRequirement.set(reqId, att);
      }
    }

    this._state = new Map();
    for (const req of openRequests) {
      this._state.set(req.id, {
        request: req,
        attachment: uploadsByRequirement.get(req.id) || null,
      });
    }
  }

  _renderHeader(allRequests) {
    if (!this._formality) return;
    if (this.hasInpiIdTarget) this.inpiIdTarget.textContent = this._formality.inpiId || "—";
    if (this.hasFormalityTypeLabelTarget) {
      const t = this._formality.formalityType;
      this.formalityTypeLabelTarget.textContent = FORMALITY_TYPE_LABELS[t] || t || "—";
    }
    if (this.hasDepositedAtTarget) {
      this.depositedAtTarget.textContent = this._formatDate(
        this._formality.submittedAt || this._formality.createdAt,
      );
    }
    // Date de la régul ouverte la plus récente.
    if (this.hasRegulReceivedAtTarget) {
      const open = allRequests.filter((r) => !r.resolvedAt);
      const latest = open.reduce((acc, r) => {
        if (!acc) return r;
        return new Date(r.receivedAt) > new Date(acc.receivedAt) ? r : acc;
      }, null);
      this.regulReceivedAtTarget.textContent = latest ? this._formatDate(latest.receivedAt) : "—";
    }
    if (this.hasIntroTarget) {
      const total = this._state.size;
      const provided = Array.from(this._state.values()).filter((s) => s.attachment).length;
      this.introTarget.textContent =
        `${total} document${total > 1 ? "s" : ""} à fournir (${provided} / ${total} déjà téléversé${provided > 1 ? "s" : ""}). `
        + "Format accepté : PDF, 10 Mo maximum par fichier.";
    }
  }

  _renderRequirements() {
    if (!this.hasRequirementsTarget) return;
    this.requirementsTarget.textContent = "";

    let position = 0;
    const total = this._state.size;
    for (const [requirementId, entry] of this._state.entries()) {
      position += 1;
      this.requirementsTarget.appendChild(this._buildRequirement(requirementId, entry, position, total));
    }

    this._refreshSubmitState();
  }

  _buildRequirement(requirementId, entry, position, total) {
    const fragment = this.cloneTemplate("tpl-inpi-requirement");
    const article = fragment.querySelector("article");
    if (!article) return fragment;

    article.dataset.requirementId = requirementId;

    const reqMeta = entry.request.requirements || {};
    const label = (reqMeta && (reqMeta.label || reqMeta.title)) || "Élément demandé par l'INPI";
    const description = (reqMeta && (reqMeta.description || reqMeta.detail)) || "";

    this.fillSlots(article, {
      position: `Élément ${position} / ${total}`,
      label,
      description,
    });

    const dropzone = article.querySelector('[data-slot="dropzone"]');
    const uploadedRow = article.querySelector('[data-slot="uploaded-row"]');
    const statusPending = article.querySelector('[data-slot="status-pending"]');
    const statusUploaded = article.querySelector('[data-slot="status-uploaded"]');
    const fileInput = article.querySelector('[data-slot="file-input"]');
    const uploadBtn = article.querySelector('[data-slot="upload-btn"]');
    const replaceBtn = article.querySelector('[data-slot="replace-btn"]');
    const errorEl = article.querySelector('[data-slot="upload-error"]');

    const renderUploaded = (att) => {
      if (statusPending) statusPending.hidden = true;
      if (statusUploaded) statusUploaded.hidden = false;
      if (dropzone) dropzone.hidden = true;
      if (uploadedRow) uploadedRow.hidden = false;
      const filenameEl = article.querySelector('[data-slot="filename"]');
      const metaEl = article.querySelector('[data-slot="file-meta"]');
      if (filenameEl) filenameEl.textContent = "📄 " + (att.filename || "Fichier téléversé");
      if (metaEl) {
        const sizeMb = att.fileSizeBytes ? (att.fileSizeBytes / (1024 * 1024)).toFixed(1) : null;
        metaEl.textContent = "Téléversé " + this._formatDate(att.uploadedAt || att.createdAt)
          + (sizeMb ? ` · ${sizeMb} Mo` : "");
      }
    };

    const renderPending = () => {
      if (statusPending) statusPending.hidden = false;
      if (statusUploaded) statusUploaded.hidden = true;
      if (dropzone) dropzone.hidden = false;
      if (uploadedRow) uploadedRow.hidden = true;
    };

    if (entry.attachment) {
      renderUploaded(entry.attachment);
    } else {
      renderPending();
    }

    if (fileInput && uploadBtn) {
      uploadBtn.addEventListener("click", () => fileInput.click());
      fileInput.addEventListener("change", async () => {
        const file = fileInput.files?.[0];
        if (!file) return;
        await this._uploadFile(requirementId, file, { uploadBtn, errorEl, fileInput, renderUploaded });
      });
    }
    if (replaceBtn && fileInput) {
      replaceBtn.addEventListener("click", () => {
        renderPending();
        fileInput.value = "";
        fileInput.click();
      });
    }

    return fragment;
  }

  // ---------------------------------------------------------------------------
  // Upload
  // ---------------------------------------------------------------------------

  async _uploadFile(requirementId, file, ui) {
    const { uploadBtn, errorEl, renderUploaded } = ui;
    if (errorEl) {
      errorEl.textContent = "";
      errorEl.hidden = true;
    }
    if (file.size > 10 * 1024 * 1024) {
      this._setRequirementError(errorEl, "Le fichier dépasse 10 Mo.");
      return;
    }
    if (file.type && file.type !== "application/pdf") {
      this._setRequirementError(errorEl, "Seuls les fichiers PDF sont acceptés.");
      return;
    }

    if (uploadBtn) {
      uploadBtn.disabled = true;
      uploadBtn.textContent = "Téléversement…";
    }
    try {
      const data = await api.inpi.regularizationUpload(this._formalityId, file, requirementId);
      const attachment = data?.attachment;
      if (!attachment) throw new Error("Réponse inattendue du serveur.");

      const entry = this._state.get(requirementId);
      if (entry) {
        entry.attachment = attachment;
      }
      renderUploaded(attachment);
      this._refreshSubmitState();
      this._renderHeader([]);
      this.showSuccess("Fichier téléversé.");
    } catch (err) {
      console.error("Upload failed", err);
      const msg = this._extractMessage(err) || "Impossible de téléverser ce fichier.";
      this._setRequirementError(errorEl, msg);
    } finally {
      if (uploadBtn) {
        uploadBtn.disabled = false;
        uploadBtn.textContent = "Téléverser un fichier";
      }
    }
  }

  _setRequirementError(errorEl, message) {
    if (!errorEl) {
      this.showError(message);
      return;
    }
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  _refreshSubmitState() {
    if (!this.hasSubmitBtnTarget) return;
    const total = this._state.size;
    const provided = Array.from(this._state.values()).filter((s) => s.attachment).length;
    const ready = total > 0 && provided === total;
    this.submitBtnTarget.disabled = !ready;
    if (this.hasSubmitHelpTarget) {
      this.submitHelpTarget.textContent = ready
        ? "Tous les éléments sont prêts. Vous pouvez renvoyer la formalité."
        : `Téléversez les ${total} élément${total > 1 ? "s" : ""} demandé${total > 1 ? "s" : ""} pour activer ce bouton (${provided} / ${total} fourni${provided > 1 ? "s" : ""}).`;
    }
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async submit() {
    if (!this.hasSubmitBtnTarget || this.submitBtnTarget.disabled) return;
    const ok = await this.confirm(
      "Renvoyer la formalité à l'INPI ?\n\nUne fois envoyée, l'INPI réinstruit votre dossier (8 à 15 jours ouvrés). Vous serez notifié·e par email à chaque étape.",
      { title: "Renvoyer la formalité", confirmLabel: "Renvoyer", cancelLabel: "Annuler" },
    );
    if (!ok) return;

    this.submitBtnTarget.disabled = true;
    const originalLabel = this.submitBtnTarget.textContent;
    this.submitBtnTarget.textContent = "Envoi en cours…";

    try {
      const data = await api.inpi.regularizationSubmit(this._formalityId);
      if (data?.redirectUrl) {
        // Modification : redirection vers procedures.inpi.fr pour re-signature.
        window.location.href = data.redirectUrl;
        return;
      }
      // Création : retour au dashboard avec confirmation.
      this.showSuccess("Formalité renvoyée. Notre équipe transmet la régularisation à l'INPI.");
      window.location.href = "/tableau-de-bord-prospect.html";
    } catch (err) {
      console.error("Submit failed", err);
      const msg = this._extractMessage(err) || "Impossible de renvoyer la formalité.";
      this.showError(msg);
      this.submitBtnTarget.disabled = false;
      this.submitBtnTarget.textContent = originalLabel;
    }
  }

  // ---------------------------------------------------------------------------
  // UI states
  // ---------------------------------------------------------------------------

  _showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = false;
    if (this.hasErrorTarget) this.errorTarget.hidden = true;
    if (this.hasContentTarget) this.contentTarget.hidden = true;
  }

  _showError(message) {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = true;
    if (this.hasErrorTarget) this.errorTarget.hidden = false;
    if (this.hasErrorMessageTarget) this.errorMessageTarget.textContent = " " + message;
    if (this.hasContentTarget) this.contentTarget.hidden = true;
  }

  _showContent() {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = true;
    if (this.hasErrorTarget) this.errorTarget.hidden = true;
    if (this.hasContentTarget) this.contentTarget.hidden = false;
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _formatDate(iso) {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    } catch (_) {
      return "—";
    }
  }

  _extractMessage(err) {
    const code = err?.data?.error?.code;
    if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
    if (err?.data?.error?.message) return err.data.error.message;
    if (err?.message) return err.message;
    return null;
  }
}
